/**
 * Seguimiento de una publicación, periódico por periódico (§10.3).
 *
 * Responde a la única pregunta que importa después de pulsar publicar: ¿salió en
 * todos? Y cuando la respuesta es no, a la siguiente: ¿en cuáles no y por qué?
 *
 * Dos reglas del §10 se cumplen aquí explícitamente:
 *
 *   - **Nunca se enseña "publicado" hasta que el destino está `published`.** Un
 *     "listo" optimista en una pantalla que va a quince periódicos es una
 *     mentira que alguien descubre horas después.
 *   - **Estado en vivo sin recargar.** Publicar tarda; una pantalla estática
 *     empuja a recargar cada pocos segundos.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '../components/app-shell.tsx';
import { Button, ErrorMessage } from '../components/ui.tsx';
import {
  ApiError,
  type Publication,
  type PublicationStatus,
  publicationsApi,
  type TargetStatus,
} from '../lib/api.ts';

/* -------------------------------------------------------------------------
 * Vocabulario visible
 * ---------------------------------------------------------------------- */

const TARGET_LABEL: Record<TargetStatus, string> = {
  pending: 'En cola',
  preparing: 'Preparando',
  prepared: 'Preparado',
  publishing: 'Publicando',
  published: 'Publicado',
  failed_retrying: 'Reintentando',
  failed: 'Falló',
  canceled: 'Cancelado',
  skipped: 'Omitido',
};

const TARGET_TONE: Record<TargetStatus, string> = {
  pending: 'bg-[var(--color-surface)] text-[var(--color-muted)]',
  preparing: 'bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]',
  prepared: 'bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]',
  publishing: 'bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]',
  published: 'bg-[var(--color-success-soft)] text-[var(--color-success)]',
  failed_retrying: 'bg-[var(--color-warning-soft)] text-[var(--color-warning)]',
  failed: 'bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
  canceled: 'bg-[var(--color-surface)] text-[var(--color-muted)]',
  skipped: 'bg-[var(--color-surface)] text-[var(--color-muted)]',
};

const PUBLICATION_LABEL: Record<PublicationStatus, string> = {
  scheduled: 'Programada',
  processing: 'En curso',
  completed: 'Completada',
  // "Parcial" y no "completada con avisos": si un periódico se quedó fuera, eso
  // es lo primero que hay que leer, no una nota al pie.
  partial: 'Parcial',
  failed: 'Fallida',
  canceled: 'Cancelada',
};

/**
 * Fecha y hora **en la zona con la que se programó**, no en la del navegador.
 *
 * Quien programa para las 8:00 de Santo Domingo y lo mira desde un portátil en
 * otra zona tiene que seguir leyendo 8:00 junto al nombre de esa zona; con la
 * hora del navegador y la etiqueta de la otra, el dato sería falso (regla #11).
 */
function formatInZone(iso: string, timeZone: string | null, options: Intl.DateTimeFormatOptions): string {
  const date = new Date(iso);

  try {
    return new Intl.DateTimeFormat('es-DO', { ...options, ...(timeZone ? { timeZone } : {}) }).format(date);
  } catch {
    // Una zona que el navegador no conoce: mejor la hora local que nada.
    return new Intl.DateTimeFormat('es-DO', options).format(date);
  }
}

/** Estados en los que ya no va a pasar nada sin que alguien actúe. */
const isSettled = (status: PublicationStatus): boolean =>
  ['completed', 'partial', 'failed', 'canceled'].includes(status);

/* -------------------------------------------------------------------------
 * Estado en vivo
 * ---------------------------------------------------------------------- */

/**
 * Sigue una publicación por SSE mientras siga en marcha.
 *
 * `EventSource` reconecta solo si la conexión se corta, que es justo lo que se
 * quiere en un portátil que se suspende. Cuando el servidor avisa de que la
 * publicación terminó, se cierra a mano: si no, `EventSource` volvería a
 * conectarse indefinidamente contra algo que ya no cambia.
 */
function useLivePublication(id: string, initial: Publication | undefined) {
  const [live, setLive] = useState<Publication | null>(null);
  const queryClient = useQueryClient();

  const settled = initial ? isSettled(live?.status ?? initial.status) : false;

  useEffect(() => {
    if (!initial || settled) {
      return;
    }

    const source = new EventSource(publicationsApi.liveUrl(id), { withCredentials: true });

    source.addEventListener('state', (event) => {
      setLive(JSON.parse((event as MessageEvent<string>).data) as Publication);
    });

    source.addEventListener('done', (event) => {
      setLive(JSON.parse((event as MessageEvent<string>).data) as Publication);
      source.close();

      // La lista de publicaciones enseña el estado final: si no se invalida,
      // volver atrás muestra "en curso" para algo que ya terminó.
      void queryClient.invalidateQueries({ queryKey: ['publications'] });
    });

    source.addEventListener('gone', () => source.close());

    return () => source.close();
    // `settled` entra a propósito: cuando pasa a true, el efecto se limpia y la
    // conexión se cierra.
  }, [id, initial, settled, queryClient]);

  return live ?? initial;
}

/* -------------------------------------------------------------------------
 * Pantalla
 * ---------------------------------------------------------------------- */

export function PublicationPage({ id }: { id: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [openTarget, setOpenTarget] = useState<string | null>(null);

  const { data: initial, isLoading } = useQuery({
    queryKey: ['publications', id],
    queryFn: () => publicationsApi.get(id),
  });

  const publication = useLivePublication(id, initial);

  const cancel = useMutation({
    mutationFn: () => publicationsApi.cancel(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['publications', id] }),
    onError: (cause: unknown) => setError(cause instanceof ApiError ? cause.message : 'No pudimos cancelar.'),
  });

  const retry = useMutation({
    mutationFn: (targetId: string) => publicationsApi.retryTarget(id, targetId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['publications', id] }),
    onError: (cause: unknown) =>
      setError(cause instanceof ApiError ? cause.message : 'No pudimos reintentar.'),
  });

  if (isLoading || !publication) {
    return (
      <AppShell title="Publicación">
        <p className="text-sm text-[var(--color-muted)]">Cargando…</p>
      </AppShell>
    );
  }

  const published = publication.targets.filter((target) => target.status === 'published').length;
  const relevant = publication.targets.filter((target) => target.status !== 'skipped').length;
  const pending = publication.targets.filter((target) =>
    ['pending', 'preparing', 'prepared', 'publishing', 'failed_retrying'].includes(target.status),
  ).length;

  // Programada y todavía no es la hora: no hay nada atascado, está esperando.
  const waitingForTime =
    publication.status === 'scheduled' &&
    publication.scheduledAt !== null &&
    new Date(publication.scheduledAt).getTime() > Date.now();

  const scheduledTime = publication.scheduledAt
    ? formatInZone(publication.scheduledAt, publication.tzDisplay, { hour: 'numeric', minute: '2-digit' })
    : '';

  return (
    <AppShell title={publication.articleTitle}>
      <ErrorMessage>{error}</ErrorMessage>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--color-line)] bg-white p-4">
        <div>
          <p className="text-sm font-medium">{PUBLICATION_LABEL[publication.status]}</p>
          <p className="text-xs text-[var(--color-muted)]">
            {published} de {relevant} periódicos
            {publication.scheduledAt
              ? ` · ${t('publication.scheduledFor', {
                  when: formatInZone(publication.scheduledAt, publication.tzDisplay, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }),
                })}${publication.tzDisplay ? ` (${publication.tzDisplay})` : ''}`
              : ''}
          </p>
          {waitingForTime ? (
            <p className="mt-1.5 max-w-prose text-xs leading-relaxed text-[var(--color-muted)]">
              {t('publication.scheduledHelp', { count: relevant })}
            </p>
          ) : null}
        </div>

        {pending > 0 ? (
          <div className="w-40">
            <Button variant="ghost" loading={cancel.isPending} onClick={() => cancel.mutate()}>
              Cancelar lo pendiente
            </Button>
          </div>
        ) : null}
      </div>

      {/*
        Se dice en voz alta que lo ya publicado no se retira. Prometer lo
        contrario sería insinuar atomicidad entre sitios (regla #10).
      */}
      {publication.status === 'partial' ? (
        <p className="mb-6 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          La noticia salió en unos periódicos y no en otros. Lo ya publicado sigue publicado: cancelar solo
          detiene lo que aún no ha salido.
        </p>
      ) : null}

      <ul className="divide-y divide-[var(--color-line)] rounded-xl border border-[var(--color-line)] bg-white">
        {publication.targets.map((target) => {
          const isOpen = openTarget === target.id;
          const waiting = waitingForTime && target.status === 'pending';

          return (
            <li key={target.id} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-3">
                <TargetChip status={target.status} scheduledTime={waiting ? scheduledTime : null} />

                <span className="min-w-0 flex-1 truncate text-sm font-medium">{target.siteName}</span>

                {target.remoteUrl ? (
                  <a
                    href={target.remoteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[var(--color-brand)] underline"
                  >
                    Ver la nota
                  </a>
                ) : null}

                {['failed', 'canceled'].includes(target.status) ? (
                  <button
                    type="button"
                    onClick={() => retry.mutate(target.id)}
                    disabled={retry.isPending}
                    className="rounded-md border border-[var(--color-line)] px-2 py-1 text-xs hover:bg-[var(--color-surface)] disabled:opacity-50"
                  >
                    Reintentar
                  </button>
                ) : null}

                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpenTarget(isOpen ? null : target.id)}
                  className="rounded-md border border-[var(--color-line)] px-2 py-1 text-xs hover:bg-[var(--color-surface)]"
                >
                  {isOpen ? 'Ocultar' : 'Detalle'}
                </button>
              </div>

              {target.lastErrorMessage ? (
                <p className="mt-2 text-xs text-[var(--color-danger)]">
                  {target.lastErrorMessage}
                  {target.lastErrorHint ? (
                    <span className="text-[var(--color-muted)]"> — {target.lastErrorHint}</span>
                  ) : null}
                </p>
              ) : null}

              {target.status === 'failed_retrying' && target.nextAttemptAt ? (
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  Siguiente intento a las {new Date(target.nextAttemptAt).toLocaleTimeString()} (intento{' '}
                  {target.attemptCount}).
                </p>
              ) : null}

              {isOpen ? <TargetTimeline publicationId={id} targetId={target.id} /> : null}
            </li>
          );
        })}
      </ul>
    </AppShell>
  );
}

/**
 * Etiqueta de estado de un periódico.
 *
 * Con `scheduledTime`, el destino está esperando su hora: se dice «Programada»
 * y cuándo, en vez de «En cola», que se lee como atascado.
 */
function TargetChip({ status, scheduledTime }: { status: TargetStatus; scheduledTime: string | null }) {
  const { t } = useTranslation();
  const waiting = scheduledTime !== null;

  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs ${
        waiting ? 'bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]' : TARGET_TONE[status]
      }`}
    >
      {waiting ? t('publication.scheduledChip', { time: scheduledTime }) : TARGET_LABEL[status]}
    </span>
  );
}

/* -------------------------------------------------------------------------
 * Línea de tiempo
 * ---------------------------------------------------------------------- */

const EVENT_LABEL: Record<string, string> = {
  queued: 'En cola',
  started: 'Intento iniciado',
  published: 'Publicado',
  error: 'Error',
  retry_scheduled: 'Reintento programado',
  canceled: 'Cancelado',
  skipped: 'Omitido',
  webhook: 'Aviso del sitio',
  reconciled: 'Reconciliado',
};

/**
 * Cada intento, cada error y cada espera, con su hora.
 *
 * Es lo que permite responder "¿por qué esta nota salió tarde en este periódico?"
 * sin adivinar. Se carga solo al desplegar: traerla para los quince destinos de
 * entrada serían quince consultas que casi nadie va a leer.
 */
function TargetTimeline({ publicationId, targetId }: { publicationId: string; targetId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['publications', publicationId, 'targets', targetId, 'events'],
    queryFn: () => publicationsApi.events(publicationId, targetId),
  });

  if (isLoading) {
    return <p className="mt-3 text-xs text-[var(--color-muted)]">Cargando la línea de tiempo…</p>;
  }

  const events = data?.events ?? [];

  if (events.length === 0) {
    return <p className="mt-3 text-xs text-[var(--color-muted)]">Todavía no hay nada registrado.</p>;
  }

  return (
    <ol className="mt-3 space-y-1.5 border-l border-[var(--color-line)] pl-3">
      {events.map((event) => (
        <li key={event.id} className="text-xs">
          <span className="text-[var(--color-muted)]">{new Date(event.at).toLocaleTimeString()}</span>{' '}
          <span className="font-medium">{EVENT_LABEL[event.kind] ?? event.kind}</span>
          {event.durationMs !== null ? (
            <span className="text-[var(--color-muted)]"> · {event.durationMs} ms</span>
          ) : null}
          {event.message ? <span className="text-[var(--color-muted)]"> — {event.message}</span> : null}
        </li>
      ))}
    </ol>
  );
}
